import { useEffect, useState } from 'react';

import type { DateRange } from '@/components/molecules/DateRangePicker/DateRangePicker';
import {
    ALL_TIME_SENTINEL_FROM,
    DATE_RANGE_PRESETS,
} from '@/components/molecules/DateRangePicker/DateRangePresets';
import {
    type UpdateFeedingDashboardConfig,
    useFeedingDashboard,
    useUpdateFeedingCageConfig,
    useUpdateFeedingDashboardConfig,
} from '@/domain/feedingDashboard';
import { usePostHogFeatureFlag } from '@/hooks/usePostHogFeatureFlag';
import { useDataSourcesHref } from '@/routes/Client/Location/pages/DataSources/useDataSourcesHref';

import type { PenSelection } from './components/PenFilterSelect/PenFilterSelect';
import { type FeedingConfigSnapshot, FeedingDashboardView } from './FeedingDashboardView';
import { usePenSelection } from './usePenSelection';

interface FeedingDashboardContainerProps {
    clientId: number;
    locationId: number;
}

function defaultDateRange(): DateRange {
    const preset = DATE_RANGE_PRESETS.find((item) => item.id === 'last4weeks');
    return preset?.calculateDates() ?? {};
}

export default function FeedingDashboardContainer(props: FeedingDashboardContainerProps) {
    const { clientId, locationId } = props;
    const [dateRange, setDateRange] = useState<DateRange>(defaultDateRange);
    // 'all' = every pen; a non-empty array = that subset; an empty array = the user
    // deselected everything, which shows nothing rather than falling back to all.
    const [selection, setSelection] = useState<PenSelection>('all');
    // Pellet fallthrough has no approved method yet (IPS req 26). The same flag hides
    // the pellet elements and tells the API to skip the pellet reads, so the two can
    // never disagree. Absent in PostHog, or still loading, means off.
    const { enabled: pelletEnabled } = usePostHogFeatureFlag('feeding-pellet-fallthrough');

    // The picker sends 1970-01-01 for "show all" so endpoints that require a bounded range
    // keep working. This one supports an open range, so the sentinel is dropped and the
    // backend spans back to the earliest day with data instead (IPS req 2). Passing it
    // through would ask for a 56-year period: ~2,900 weekly buckets and every query
    // scanning from 1970.
    const from = dateRange.dateFrom === ALL_TIME_SENTINEL_FROM ? undefined : dateRange.dateFrom;

    // Deselecting every pen is reachable in one click (the selector's "all" toggle emits
    // []), and it has to mean "nothing" rather than "everything". It cannot be expressed
    // over the wire: an omitted cageIds means every pen to the API, and `?cageIds=` fails
    // DTO validation, so the request is skipped here instead.
    const noPensSelected = selection !== 'all' && selection.length === 0;

    const { data, isLoading, isPreviousData, error, isFetching, refetch, pollingSlow } =
        useFeedingDashboard(
            locationId,
            {
                from,
                to: dateRange.dateTo,
                cageIds: selection !== 'all' && selection.length > 0 ? selection : undefined,
                includeFallthrough: pelletEnabled,
            },
            { enabled: !noPensSelected }
        );

    // keepPreviousData keeps the last response cached against its own query key, so
    // without this the charts would keep showing the previous selection's data while
    // nothing is selected.
    const overview = noPensSelected ? undefined : data;

    // Every response lists the whole period's pens, so the selector follows the period
    // and a selection that names pens the period does not have is corrected.
    const penOptions = usePenSelection(selection, setSelection, data, isPreviousData);

    // The settings panel is site-level, so it stays reachable with nothing selected.
    // Held in state for the same reason penOptions is: the response it came from is gone
    // while the filter is empty, and hiding the panel would make the site's own settings
    // unreachable until the user re-picked a pen.
    const [lastConfig, setLastConfig] = useState<FeedingConfigSnapshot>();
    useEffect(() => {
        if (!data) return;
        setLastConfig({
            config: data.config,
            pens: data.pens.map((pen) => ({
                cageId: pen.cageId,
                cageName: pen.cageName,
                config: pen.config,
            })),
        });
    }, [data]);

    // Null for a location admin or while the flag is off, which leaves every link out.
    const dataSourcesHref = useDataSourcesHref(clientId, locationId);

    const configMutation = useUpdateFeedingDashboardConfig(locationId);
    const penConfigMutation = useUpdateFeedingCageConfig(locationId);

    // Returns a promise so the config panel can close only on success and surface
    // an error if the PUT fails (otherwise a failed save closes silently).
    const handleSaveConfig = (update: UpdateFeedingDashboardConfig): Promise<void> => {
        if (Object.keys(update).length === 0) return Promise.resolve();
        return configMutation.mutateAsync(update).then(() => undefined);
    };

    const handleSavePenConfig = (
        cageId: number,
        update: UpdateFeedingDashboardConfig
    ): Promise<void> => {
        if (Object.keys(update).length === 0) return Promise.resolve();
        return penConfigMutation.mutateAsync({ cageId, update }).then(() => undefined);
    };

    return (
        <FeedingDashboardView
            overview={overview}
            configFallback={lastConfig}
            noPensSelected={noPensSelected}
            pollingSlow={pollingSlow}
            updating={isPreviousData && !noPensSelected}
            pelletEnabled={pelletEnabled}
            loading={isLoading}
            error={error}
            onRetry={refetch}
            isRetrying={isFetching}
            penOptions={penOptions}
            penSelection={selection}
            dateRange={dateRange}
            savingConfig={configMutation.isPending || penConfigMutation.isPending}
            onPenChange={setSelection}
            onDateChange={setDateRange}
            onSaveConfig={handleSaveConfig}
            onSavePenConfig={handleSavePenConfig}
            dataSourcesHref={dataSourcesHref}
        />
    );
}
